import { Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type FileDragOverlayProps = {
  showOverlay: boolean;
  onFile: (file: File) => void;
  onNoFile: () => void;
  onLostDrop: () => void;
};

// Window-level file drop target. It owns its drag state so a drag never re-renders the app tree:
// Chrome silently drops the `drop` event if the element under the cursor is replaced mid-drag.
export function FileDragOverlay({ showOverlay, onFile, onNoFile, onLostDrop }: FileDragOverlayProps) {
  const [active, setActive] = useState(false);
  const latest = useRef({ showOverlay, onFile, onNoFile, onLostDrop });
  latest.current = { showOverlay, onFile, onNoFile, onLostDrop };

  useEffect(() => {
    let idleTimer: number | undefined;
    const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer?.types ?? []).includes('Files');
    const end = () => {
      window.clearTimeout(idleTimer);
      setActive(false);
    };
    const onOver = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      if (latest.current.showOverlay) setActive(true);
      window.clearTimeout(idleTimer);
      // dragover repeats about every 50 ms while a drag is over the page. If it just stops, with no
      // drop or leave, Chrome lost the drop (seen when it re-activates its window mid-drag from Finder).
      idleTimer = window.setTimeout(() => {
        end();
        latest.current.onLostDrop();
      }, 400);
    };
    // Bubble phase: runs after element handlers, so nothing can leave a file drag with a refused drop effect.
    const settle = (event: DragEvent) => {
      if (!hasFiles(event) || !event.dataTransfer) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    };
    const onLeave = (event: DragEvent) => {
      if (hasFiles(event) && event.relatedTarget === null) end();
    };
    const onDrop = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      end();
      const transfer = event.dataTransfer;
      const file =
        transfer?.files?.[0] ?? Array.from(transfer?.items ?? []).find((item) => item.kind === 'file')?.getAsFile();
      if (file) latest.current.onFile(file);
      else latest.current.onNoFile();
    };
    window.addEventListener('dragenter', onOver, true);
    window.addEventListener('dragover', onOver, true);
    window.addEventListener('dragover', settle);
    window.addEventListener('dragleave', onLeave, true);
    window.addEventListener('dragend', end, true);
    window.addEventListener('drop', onDrop, true);
    return () => {
      window.clearTimeout(idleTimer);
      window.removeEventListener('dragenter', onOver, true);
      window.removeEventListener('dragover', onOver, true);
      window.removeEventListener('dragover', settle);
      window.removeEventListener('dragleave', onLeave, true);
      window.removeEventListener('dragend', end, true);
      window.removeEventListener('drop', onDrop, true);
    };
  }, []);

  return (
    <div className="file-drag-overlay" aria-hidden="true" hidden={!active}>
      <div className="file-drag-card">
        <Upload size={40} />
        <strong>Drop CSV to open</strong>
        <span>Release to replace the current datalog</span>
      </div>
    </div>
  );
}
